// Försäkra bilen — the buyer's page after the seller has reported the ownership transfer.
//
// A DEMO SURFACE, ported from the mockup's Insurance page. Everything on it is hard-coded in the
// front end (insuranceDemoData.js): the offers, the vehicle, the seller, the policy. Nothing is
// read from or saved to the backend, and "Teckna försäkring" signs nothing.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import RegistrationPlate from "../../common/RegistrationPlate";
import { formatRegNumber } from "../../../lib/format.jsx";
import { StatusDot, BackArrow } from "../StatusMark";
import { H1_CLASS, LEAD_CLASS, META_CLASS, PRIMARY_ACTION, ACTION_ROW } from "../payment/PaymentParts";
import { OWNERSHIP_CONTENT_MAX } from "../ownership/OwnershipParts";
import {
  ACKNOWLEDGEMENTS,
  COVERAGE_LEVELS,
  DEFAULT_LEVEL,
  DEMO_POLICY_NUMBER,
  DEMO_SELLER_NAME,
  DEMO_TRANSFER_DATE,
  DEMO_VEHICLE,
  fixtureText,
  makeCopy,
  offersForLevel,
  sortOffers,
} from "./insuranceDemoData";

/** The demo's "signing" takes a moment, so the submitting state is seen. */
export const INSURANCE_SUBMIT_MS = 1400;
/** How long the buyer reads "the insurance is taken out" before the purchase-complete page opens. */
export const INSURANCE_TO_COMPLETION_MS = 3000;

const TEXT_ACTION = "ds-action-text";

const RadioMark = ({ selected }) => (
  <span
    aria-hidden="true"
    className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${
      selected ? "border-ds-ink" : "border-[rgba(20,36,46,.42)]"
    }`}
  >
    {selected && <span className="h-2 w-2 rounded-full bg-ds-ink" />}
  </span>
);

/** Arrow keys move the choice inside a radio group, as the platform's own radios do. */
function onRadioArrows(e) {
  const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
  const back = e.key === "ArrowUp" || e.key === "ArrowLeft";
  if (!forward && !back) return;
  const radios = [...e.currentTarget.querySelectorAll('[role="radio"]:not([disabled])')];
  const at = radios.indexOf(document.activeElement);
  if (at < 0) return;
  e.preventDefault();
  const target = radios[(at + (forward ? 1 : -1) + radios.length) % radios.length];
  target.focus();
  target.click();
}

const rovingTab = (active, index, anyActive) => (active || (!anyActive && index === 0) ? 0 : -1);

function RegisterRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-0.5 border-b border-ds-rule-row py-3.5">
      <dt className="ds-label m-0">{label}</dt>
      <dd className="m-0 text-right text-[15px] font-semibold text-ds-ink">{children}</dd>
    </div>
  );
}

function DetailList({ title, items }) {
  return (
    <div>
      <p className="ds-label m-0">{title}</p>
      <ul className="mt-1.5 space-y-1 p-0">
        {items.map((item) => (
          <li key={item} className="max-w-[62ch] list-none text-[13.5px] leading-relaxed text-ds-text">{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default function DealInsuranceStage({ onFinish }) {
  const { i18n } = useTranslation();
  const language = i18n.language && i18n.language.startsWith("en") ? "en" : "sv";
  const c = useMemo(() => makeCopy(language), [language]);
  const fx = (text) => fixtureText(text, language);
  const locale = language === "en" ? "en-GB" : "sv-SE";
  const sek = (n) => `${n.toLocaleString("sv-SE")} kr`;
  const monthly = (price) => c("offer.monthly", { price: String(price) });
  // The price is drawn large, so the template is read as the words either side of it.
  const [monthlyPre, monthlyPost] = c("offer.monthly", { price: "\u0001" }).split("\u0001");
  const monthlyParts = { pre: monthlyPre, post: monthlyPost };
  const plate = formatRegNumber(DEMO_VEHICLE.registrationNumber);
  const startDate = new Date(`${DEMO_TRANSFER_DATE}T12:00:00Z`).toLocaleDateString(locale, {
    day: "numeric", month: "long", year: "numeric",
  });

  const [step, setStep] = useState("compare"); // compare | finalize | done
  const [level, setLevel] = useState(DEFAULT_LEVEL);
  const [selectedId, setSelectedId] = useState(null);
  const [openDetailsId, setOpenDetailsId] = useState(null);
  const [confirmingExisting, setConfirmingExisting] = useState(false);
  const [acks, setAcks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // "signed" | "own"
  const [preview, setPreview] = useState(null);
  const [announcement, setAnnouncement] = useState("");

  const offers = useMemo(() => sortOffers(offersForLevel(level), "price"), [level]);
  const selected = offers.find((o) => o.id === selectedId) || null;
  const shared = useMemo(
    () => (offers.length < 2 ? [] : (offers[0].highlights || []).filter((h) => offers.every((o) => o.highlights.includes(h)))),
    [offers],
  );
  const lowest = (lvl) => Math.min(...offersForLevel(lvl).map((o) => o.monthlySek));
  const levelFull = (lvl) => c(`level.${lvl}.full`);
  const offerCount = (n) => (n === 1 ? c("offerCount.one") : c("offerCount.many", { count: String(n) }));
  const openAcks = ACKNOWLEDGEMENTS.filter((a) => !acks[a.id]);

  const timerRef = useRef(null);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const headingRef = useRef(null);
  useEffect(() => {
    if (step === "finalize" || step === "done") {
      window.scrollTo(0, 0);
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [step]);

  const chooseLevel = (next) => {
    setLevel(next);
    setSelectedId(null);
    setOpenDetailsId(null);
    setAnnouncement(`${offerCount(offersForLevel(next).length)} · ${levelFull(next)}`);
  };

  const sign = () => {
    setSubmitting(true);
    timerRef.current = window.setTimeout(() => {
      setSubmitting(false);
      setOutcome("signed");
      setStep("done");
    }, INSURANCE_SUBMIT_MS);
  };

  // What the buyer did about insurance, handed to the closing page in memory (never saved).
  const result = () =>
    outcome === "signed" && selected
      ? { kind: "signed", level: selected.level, insurer: selected.insurer, monthlySek: selected.monthlySek, startDate: DEMO_TRANSFER_DATE }
      : { kind: "own" };

  // A buyer with their own insurance has nothing to read back: the step ends and the purchase
  // is complete. A policy taken out here is shown for a moment first (see below).
  const finishExisting = () => onFinishRef.current({ kind: "own" });

  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  useEffect(() => {
    if (step !== "done" || outcome !== "signed") return undefined;
    const timer = window.setTimeout(() => onFinishRef.current(result()), INSURANCE_TO_COMPLETION_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, outcome]);

  const band = (
    <div className="mb-8 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-t-2 border-ds-ink pt-4">
      <div className="flex min-h-[46px] min-w-0 items-center gap-4" data-testid="insurance-vehicle">
        <RegistrationPlate value={DEMO_VEHICLE.registrationNumber} size="row" />
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-ds-ink">{DEMO_VEHICLE.make} {DEMO_VEHICLE.model}</div>
          <div className="ds-meta">{DEMO_VEHICLE.year} · {c("vehicle")}</div>
        </div>
      </div>
    </div>
  );

  const zmarta = (
    <p className="ds-meta m-0" data-testid="zmarta-attribution">
      {c("zmarta")} <span className="font-medium text-ds-ink">Zmarta</span>
    </p>
  );

  const termsToggle = (offer, controls) => {
    const open = openDetailsId === offer.id;
    return (
      <button
        type="button"
        onClick={() => setOpenDetailsId(open ? null : offer.id)}
        aria-expanded={open}
        aria-controls={controls}
        className="ds-doc-edit -my-3 inline-flex min-h-[44px] items-center gap-1"
        data-testid="offer-terms-toggle"
      >
        {open ? c("terms.hide") : c("terms.show")}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
    );
  };

  const documents = (offer) => (
    <div>
      {offer.documents.map((title) => (
        <div key={title} className="border-b border-ds-rule-row last:border-b-0">
          <button
            type="button"
            onClick={() => setPreview({ offer, title })}
            aria-label={c("doc.openAria", { document: fx(title).toLowerCase(), insurer: offer.insurer })}
            className="flex min-h-[44px] w-full flex-col items-start py-2 text-left"
          >
            <span className="text-[14px] font-medium text-ds-ink">{fx(title)}</span>
            <span className="ds-meta">PDF · {offer.insurer}</span>
          </button>
        </div>
      ))}
    </div>
  );

  const details = (offer) => (
    <div className="space-y-4 border-t border-ds-rule-row pt-3.5" data-testid="offer-terms">
      <DetailList title={c("detail.covers")} items={offer.covers.map(fx)} />
      <DetailList title={c("detail.exclusions")} items={offer.exclusions.map(fx)} />
      <div>
        <p className="ds-label m-0">{c("detail.deductibles")}</p>
        <dl className="m-0 mt-1.5">
          {offer.deductibles.map((d) => (
            <div key={d.label} className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-0.5 py-1">
              <dt className="ds-meta m-0">
                {fx(d.label)}
                {d.note && <span className="block max-w-[46ch]">{fx(d.note)}</span>}
              </dt>
              <dd className="m-0 text-[13.5px] tabular-nums text-ds-ink">{sek(d.amountSek)}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div>
        <p className="ds-label m-0">{c("detail.documents")}</p>
        <div className="mt-1">{documents(offer)}</div>
      </div>
    </div>
  );

  const previewDialog = preview && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,36,46,.45)] p-4" role="presentation"
      onClick={() => setPreview(null)}>
      <div role="dialog" aria-modal="true" aria-label={fx(preview.title)} onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-[12px] border border-ds-rule-edge bg-ds-sheet p-6">
        <p className="m-0 text-[17px] font-semibold text-ds-ink">{fx(preview.title)}</p>
        <p className="ds-meta m-0 mt-1">{c("doc.previewMeta", { insurer: preview.offer.insurer })}</p>
        <p className="ds-meta m-0 mt-1">{DEMO_VEHICLE.make} {DEMO_VEHICLE.model} · {plate}</p>
        <div className="mt-4 space-y-2 border-t border-ds-rule-row pt-4">
          {preview.offer.covers.map((line) => (
            <p key={line} className="m-0 text-[13.5px] leading-relaxed text-ds-text">{fx(line)}</p>
          ))}
        </div>
        <p className={`${META_CLASS} mt-4`}>{c("doc.previewNote")}</p>
        <div className="mt-5">
          <button type="button" className="ds-action-outline ds-action-compact" onClick={() => setPreview(null)}>
            {c("doc.close")}
          </button>
        </div>
      </div>
    </div>
  );

  const stage = (children) => (
    <section className="mx-auto w-full pb-8" style={{ maxWidth: OWNERSHIP_CONTENT_MAX }}
      data-testid="deal-insurance-stage">
      {children}
      <p className="sr-only" role="status" aria-live="polite" data-testid="insurance-announcement">{announcement}</p>
      {previewDialog}
    </section>
  );

  /* ---- DONE -------------------------------------------------------------------------------- */
  if (step === "done") {
    // Only a policy taken out here is shown: "I already have insurance" ends the step at once.
    return stage(
      <div data-testid="insurance-success">
        {band}
        <p className="m-0 mb-2 flex items-center gap-2">
          <StatusDot tone="green" />
          <span className="text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{c("success.kicker")}</span>
        </p>
        <h1 ref={headingRef} tabIndex={-1} className={`ds-arrival-heading ${H1_CLASS} outline-none`}>
          {c("success.title")}
        </h1>
        <p className={LEAD_CLASS}>
          {c("success.lead", { level: c(`level.${selected.level}.inSentence`), insurer: selected.insurer })}
        </p>
        <dl className="m-0 mt-7 border-t border-[rgba(20,36,46,.25)]" data-testid="success-summary">
            <RegisterRow label={c("row.insurer")}>{selected.insurer}</RegisterRow>
            <RegisterRow label={c("row.level")}>{levelFull(selected.level)}</RegisterRow>
            <RegisterRow label={c("row.price")}><span className="tabular-nums">{monthly(selected.monthlySek)}</span></RegisterRow>
            <RegisterRow label={c("row.validFrom")}>{startDate}</RegisterRow>
            <RegisterRow label={c("row.regNumber")}>{plate}</RegisterRow>
            <RegisterRow label={c("row.policyNumber")}><span className="tabular-nums">{DEMO_POLICY_NUMBER}</span></RegisterRow>
        </dl>
        <div className={ACTION_ROW}>
          <button type="button" onClick={() => onFinish(result())} className={PRIMARY_ACTION} data-testid="insurance-finish">
            {c("success.cta")}
          </button>
        </div>
        <div className="mt-8 border-t border-ds-rule-row pt-4">
          <p className={META_CLASS}>{c("roleNote")}</p>
        </div>
      </div>,
    );
  }

  /* ---- SIGN -------------------------------------------------------------------------------- */
  if (step === "finalize" && selected) {
    const missing = openAcks.length === 0 ? [] : [openAcks.length === 1 ? c("missing.ack.one") : c("missing.ack.many")];
    return stage(
      <div data-testid="insurance-finalize">
        {band}
        <h1 ref={headingRef} tabIndex={-1} className={`ds-arrival-heading ${H1_CLASS} outline-none`}>{c("sign.title")}</h1>

        <dl className="m-0 mt-7 border-t border-[rgba(20,36,46,.25)]" data-testid="finalize-summary">
          <RegisterRow label={c("row.insurer")}>{selected.insurer}</RegisterRow>
          <RegisterRow label={c("row.level")}>{levelFull(selected.level)}</RegisterRow>
          <RegisterRow label={c("row.includes")}>
            <span className="font-normal text-ds-text">{selected.highlights.map(fx).join(" · ")}</span>
          </RegisterRow>
          <RegisterRow label={c("row.price")}>
            <span className="text-[17px] tabular-nums">{monthly(selected.monthlySek)}</span>
            <span className="ds-meta block font-normal tabular-nums">{c("perYear", { amount: sek(selected.annualSek) })}</span>
          </RegisterRow>
          <RegisterRow label={c("row.deductible")}><span className="tabular-nums">{sek(selected.deductibleSek)}</span></RegisterRow>
        </dl>
        <div className="mt-4">
          {termsToggle(selected, "signing-details")}
          {openDetailsId === selected.id && <div id="signing-details" className="mt-3">{details(selected)}</div>}
        </div>

        <div className="mt-8" data-testid="insurance-start">
          <p className="ds-label m-0">{c("start.label")}</p>
          <div className="mt-2 border-t border-ds-rule-row">
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-ds-rule-row py-3.5">
              <p className="m-0 text-[15px] font-semibold text-ds-ink">{startDate}</p>
              <p className="ds-meta m-0 max-w-[52ch]">{c("start.note")}</p>
            </div>
          </div>
        </div>

        <div className="mt-8" data-testid="finalize-terms">
          <p className="ds-label m-0">{c("sign.terms")}</p>
          <div className="mt-2 border-t border-ds-rule-row">
            <div className="border-b border-ds-rule-row py-1">{documents(selected)}</div>
            {ACKNOWLEDGEMENTS.map((a) => {
              const ticked = Boolean(acks[a.id]);
              return (
                <button
                  key={a.id}
                  type="button"
                  role="checkbox"
                  aria-checked={ticked}
                  disabled={submitting}
                  onClick={() => setAcks((cur) => ({ ...cur, [a.id]: !ticked }))}
                  data-testid="finalize-requirement"
                  className="flex min-h-[44px] w-full items-start gap-3 border-b border-ds-rule-row py-3.5 text-left disabled:opacity-50"
                >
                  <span aria-hidden="true"
                    className={`mt-[1px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
                      ticked ? "border-ds-ink bg-ds-ink" : "border-[rgba(20,36,46,.42)]"
                    }`}>
                    {ticked && <Check size={12} strokeWidth={3} className="text-white" />}
                  </span>
                  <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-ds-ink">{fx(a.label)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {submitting ? (
          <div className="mt-8 max-w-sm" data-testid="insurance-submitting">
            <p className="m-0 text-[15px] font-semibold text-ds-ink" role="status">{c("sign.submitting")}</p>
            <div className="mt-3 h-[2px] w-full overflow-hidden bg-ds-rule-row" aria-hidden="true">
              <div className="h-full w-1/3 animate-pulse bg-ds-ink" />
            </div>
          </div>
        ) : (
          <>
            <div className={ACTION_ROW}>
              <button type="button" onClick={sign} disabled={openAcks.length > 0} className={PRIMARY_ACTION}
                data-testid="insurance-sign">
                {c("sign.cta")}
              </button>
              <button type="button" onClick={() => setStep("compare")}
                className={`${TEXT_ACTION} justify-center sm:justify-start`} data-testid="insurance-change-choice">
                <BackArrow />
                <span>{c("sign.change")}</span>
              </button>
            </div>
            {missing.length > 0 && (
              <p className={`${META_CLASS} mt-3`} data-testid="insurance-missing">{c("sign.missing", { items: missing.join(", ") })}</p>
            )}
            <p className={`${META_CLASS} mt-3`}>{c("sign.binding")}</p>
          </>
        )}

        <div className="mt-6 border-t border-ds-rule-row pt-4">
          <p className={META_CLASS}>{c("roleSentence", { insurer: selected.insurer })}</p>
        </div>
      </div>,
    );
  }

  /* ---- COMPARE ----------------------------------------------------------------------------- */
  const alreadyHave = confirmingExisting ? (
    <div className="mt-5 border-b border-t border-ds-rule-row pb-4 pt-4 sm:mt-6" data-testid="insurance-already-have-block">
      <p className="m-0 text-[15px] font-semibold text-ds-ink">{c("own.prompt", { plate })}</p>
      <p className={`${META_CLASS} mt-1`}>{c("own.note")}</p>
      <div className={`${ACTION_ROW} !mt-4`}>
        <button type="button" onClick={finishExisting} className={PRIMARY_ACTION} data-testid="insurance-already-have-confirm">
          {c("own.confirm")}
        </button>
        <button type="button" onClick={() => setConfirmingExisting(false)}
          className={`${TEXT_ACTION} justify-center sm:justify-start`} data-testid="insurance-already-have-cancel">
          <span className="ds-underline">{c("back")}</span>
        </button>
      </div>
    </div>
  ) : (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-b border-t border-ds-rule-row py-2 sm:mt-6"
      data-testid="insurance-already-have-block">
      <p className="m-0 text-[14px] leading-snug text-ds-text">{c("own.ask")}</p>
      <button type="button" onClick={() => setConfirmingExisting(true)} className="ds-action-outline ds-action-compact shrink-0"
        data-testid="insurance-already-have">
        {c("own.open")}
      </button>
    </div>
  );

  return stage(
    <>
      {band}
      <p className="m-0 mb-2 flex items-center gap-2" data-testid="ownership-ack">
        <StatusDot tone="green" />
        <span className="text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{c("ack", { seller: DEMO_SELLER_NAME })}</span>
      </p>
      <h1 className={H1_CLASS}>{c("title")}</h1>
      <p className={LEAD_CLASS} data-testid="insurance-lead">{c("trafficRule")}</p>
      {alreadyHave}

      <div className="mt-6">
        <div data-testid="coverage-level">
          <p className="ds-label m-0 mb-2">{c("level.aria")}</p>
          <div role="radiogroup" aria-label={c("level.aria")} onKeyDown={onRadioArrows}
            className="grid grid-cols-3 border-t border-[rgba(20,36,46,.25)] sm:block">
            {COVERAGE_LEVELS.map((lvl, i) => {
              const active = lvl === level;
              return (
                <button
                  key={lvl}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  tabIndex={rovingTab(active, i, true)}
                  onClick={() => chooseLevel(lvl)}
                  data-level={lvl}
                  className={`flex min-h-[44px] w-full flex-col items-start gap-0.5 border-b-2 px-2 py-2.5 text-left transition-colors sm:flex-row sm:items-center sm:gap-3 sm:border-b sm:border-ds-rule-row ${
                    active ? "border-ds-ink bg-[rgba(20,36,46,.028)]" : "border-ds-rule-row"
                  }`}
                >
                  <span className="hidden sm:flex"><RadioMark selected={active} /></span>
                  <span className={`min-w-0 text-[15px] font-semibold sm:flex-1 sm:text-ds-ink ${active ? "text-ds-ink" : "text-ds-text"}`}>
                    {c(`level.${lvl}.label`)}
                  </span>
                  <span className="text-[12.5px] tabular-nums leading-snug text-ds-text sm:whitespace-nowrap sm:text-[13.5px]">
                    {c("level.from", { price: monthly(lowest(lvl)) })}
                  </span>
                </button>
              );
            })}
          </div>
          <p className={`${META_CLASS} mt-2.5`} data-testid="coverage-level-note">{c(`level.${level}.note`)}</p>
        </div>

        <div className="mt-7 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-0.5 sm:mt-8">
          <p className="m-0 text-[15px] font-semibold text-ds-ink" data-testid="insurance-offers-count">
            {offerCount(offers.length)} · {levelFull(level)}
          </p>
          {zmarta}
        </div>
        {shared.length > 0 && (
          <p className={`${META_CLASS} mt-1.5`} data-testid="insurance-offers-common">
            {`${c("offers.common")}: ${shared.map(fx).join(" · ")}`}
          </p>
        )}
      </div>

      <ul role="radiogroup" aria-label={c("offers.aria")} onKeyDown={onRadioArrows}
        className="m-0 mt-3 list-none border-t border-[rgba(20,36,46,.25)] p-0" data-testid="insurance-offers"
        data-count={offers.length}>
        {offers.map((offer, i) => {
          const isSel = offer.id === selectedId;
          const rowHighlights = offer.highlights.filter((h) => !shared.includes(h));
          return (
            <li key={offer.id} className={`border-b border-ds-rule-row transition-colors ${isSel ? "bg-[rgba(20,36,46,.028)]" : ""}`}
              data-testid="insurance-offer" data-offer={offer.id} data-selected={isSel ? "true" : "false"}>
              <button type="button" role="radio" aria-checked={isSel} tabIndex={rovingTab(isSel, i, Boolean(selected))}
                onClick={() => setSelectedId(offer.id)} className="flex w-full items-start gap-3.5 px-2 pb-3 pt-4 text-left">
                <span className="mt-[5px] flex"><RadioMark selected={isSel} /></span>
                <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,1fr)_150px_150px]">
                  <span className="text-[16px] font-semibold text-ds-ink">{offer.insurer}</span>
                  <span className="order-3 col-span-2 text-[13.5px] text-ds-text sm:order-none sm:col-span-1 sm:text-right">
                    {c("offer.deductible", { amount: sek(offer.deductibleSek) })}
                  </span>
                  <span className="whitespace-nowrap text-right text-[13.5px] font-medium text-ds-text">
                    {monthlyParts.pre}
                    <span className="text-[21px] font-semibold leading-none tabular-nums text-ds-ink">{offer.monthlySek}</span>
                    {monthlyParts.post}
                  </span>
                  {rowHighlights.length > 0 && (
                    <span className="ds-meta order-4 col-span-2 block leading-relaxed sm:col-span-3">
                      {rowHighlights.map(fx).join(" · ")}
                    </span>
                  )}
                </span>
              </button>
              <div className="px-2 pb-3 pl-[40px]">
                {termsToggle(offer, `offer-details-${offer.id}`)}
                {openDetailsId === offer.id && <div id={`offer-details-${offer.id}`} className="mt-3">{details(offer)}</div>}
              </div>
            </li>
          );
        })}
      </ul>

      <div className={ACTION_ROW}>
        <button type="button" onClick={() => setStep("finalize")} disabled={!selected} className={PRIMARY_ACTION}
          data-testid="insurance-continue">
          {selected ? c("cta.continueWith", { insurer: selected.insurer }) : c("cta.choose")}
        </button>
        {selected && <p className={`${META_CLASS} text-center sm:text-left`}>{c("cta.nothingYet")}</p>}
      </div>
    </>,
  );
}
